import { ChangeDetectorRef, Component, inject, Injectable } from '@angular/core';
import { MediaObserver } from '@angular/flex-layout';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { ICustomer } from 'src/app/domain/customer/ICustomer';
import moment from 'moment';
import { take, firstValueFrom, finalize } from 'rxjs';
import { MessagesEnum } from 'src/app/domain/enums/messages.enum';
import { ITableResponsive } from 'src/app/domain/ITableResponsive';
import { ParcelOriginIdEnum } from 'src/app/domain/registration/enums/parcel-origin.enum';
import { ParcelTypeIdEnum } from 'src/app/domain/registration/enums/parcel-type.enum';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { TypeComponentFieldEnum } from 'src/app/domain/registration/enums/type-component-dynamic.enum';
import { IBill } from 'src/app/domain/registration/models/Bill';
import { AbstractCrudIndex } from 'src/app/shared/components/crud/abstract-crud-index';
import { DialogConfirmComponent } from 'src/app/shared/components/dialog-confirm/dialog-confirm.component';
import { GlobalUtils } from 'src/app/shared/global-utils';
import { PermissionService } from 'src/app/shared/permission.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ReasonDialog } from '../../shared/reason-dialog/reason-dialog.component';
import { TenantService } from '../../tenants/crud-service';
import {
  COLUMNS_TO_SHOW_MOBILE,
  COLUMNS_TO_SHOW_WEB,
  TABLE_CONFIG_COLUMNS,
} from '../crud-config';
import { CustomerService } from '../crud-service';
import { CustomerParcelCESComponent } from '../../shared/generate-parcels/crud-ces/crud-ces.component';
import { CustomerParcelService } from '../../shared/generate-parcels/crud-service';
import { CustomerToUpdatePlansComponent } from './customer-to-update-plans/customer-to-update-plans.component';
import { ComponentOptionsComponent } from '../component-options/component-options.component';
import { ContractsService } from '../../contracts/crud-service';
import { ContractsListDialogComponent } from '../../contracts/components/contract-dialog/contract-list-modal/contracts-list-dialog.component';
import { ContractsDialogComponent } from '../../contracts/components/contract-dialog/contracts-dialog.component';
import { DeviceTypeSelectDialogComponent } from 'src/app/shared/components/dynamic-form/components/form-image/device-type-select-dialog/device-type-select-dialog.component';
import { S3_URL_BASE } from 'src/environments/environment';
import { CustomerContractGenerationService } from '../../contracts/services/customer-contract-generation.service';
import { AccountDashboardService } from '../../accounts-dashboard/account-dashboard.service';
import { ReportPreviewComponent } from './report-preview/report-preview.component';

@Injectable()
export class CustomerCrudIndex
  extends AbstractCrudIndex<ICustomer>
  implements ITableResponsive {
  totalizers: {
    selectTotal: number;
    registrationTotal: number;
    activeTotal: number;
    deactiveTotal: number;
    lockTotal: number;
  } = {
      selectTotal: 0,
      registrationTotal: 0,
      activeTotal: 0,
      deactiveTotal: 0,
      lockTotal: 0,
    };

  override configColumns = TABLE_CONFIG_COLUMNS;
  filterFormValues: any = null;
  protected accountDashboardService = inject(AccountDashboardService);

  editParams = [
    'cnpj',
    'cpf',
    'companyName',
    'tradingName',
    'stateRegistration',
    'municipalRegistration',
    'phone',
    'cellPhone',
    'email',
  ];

  constructor(
    public override dialog: MatDialog,
    public override service: CustomerService,
    public contractsService: ContractsService,
    public override toastService: ToastService,
    public override mediaObserver: MediaObserver,
    public override router: Router,
    public tenantService: TenantService,
    public customerParcelService: CustomerParcelService,
    public permissionService: PermissionService,
    public cdr: ChangeDetectorRef,
    public customerContractGenerationService: CustomerContractGenerationService,
  ) {
    super(dialog, service, toastService, mediaObserver, router);
    this.title = service.pluralTitle.toUpperCase();
    mediaObserver.asObservable().subscribe((res) => {
      this.is_mobile = res[0].mqAlias === 'sm' || res[0].mqAlias === 'xs';
      this.displayedColumns = this.is_mobile
        ? COLUMNS_TO_SHOW_MOBILE
        : COLUMNS_TO_SHOW_WEB;
    });
  }

  override loadList(isReset: boolean = false) {
    // Se for um reset de filtro, ignoramos qualquer filtro armazenado
    if (isReset) {
      let params = this.params;
      this.service.listAll(params).subscribe((dataWithPagination: any) => {
        const [data, length, actives, deactives, locks] = dataWithPagination;
        this.length = length;
        this.totalizers = {
          ...this.totalizers,
          registrationTotal: length,
          activeTotal: actives,
          deactiveTotal: deactives,
          lockTotal: locks,
        };

        this.dataSource = new MatTableDataSource<any>(this.getListData(data));
      });
      return;
    }

    let params = this.params;

    this.service.listAll(params).subscribe((dataWithPagination: any) => {
      const [data, length, actives, deactives, locks] = dataWithPagination;
      this.length = length;
      this.totalizers = {
        ...this.totalizers,
        registrationTotal: length,
        activeTotal: actives,
        deactiveTotal: deactives,
        lockTotal: locks,
      };

      this.dataSource = new MatTableDataSource<any>(this.getListData(data));
    });
  }

  public getListData(data: any[]) {
    // Ordena os dados pelo código antes de processar
    data.sort((a, b) => {
      const codeA = parseInt(a.code) || 0;
      const codeB = parseInt(b.code) || 0;
      return codeA - codeB;
    });

    // Inicializa os dados com um estado de carregamento visível
    const initialData = data.map((item: any) => {
      item['document'] = item.cnpj || item.cpf;
      item['name'] = item.companyName || item.name;
      item['isLicenseGenerated'] = item.tenantId !== null;
      item['hideEdit'] = !item.allowEdit;
      item['hideDelete'] = !item.allowDelete;
      // item['city'] = item.city.toUpperCase();
      item.code = `${item.code}`.padStart(4, '0');
      // Força isLock a ser booleano
      item.isLock = !!item.isLock;
      item['createdAt'] = moment(item.createdAt).format('DD/MM/YYYY');
      // Define um valor inicial para isContractSigned
      item['isContractSigned'] = 'LOADING'; // Estado inicial
      item['isContractGenerated'] = 'LOADING'; // Pode precisar de lógica similar
      return item;
    });

    // Atualiza a tabela imediatamente com os valores iniciais
    this.dataSource = new MatTableDataSource<any>(initialData);

    // Agora busca os dados de contrato de forma assíncrona
    const promises = initialData.map(async (item) => {
      try {
        // Aguarda a resposta da API
        const contractStatus = await firstValueFrom(
          this.contractsService.getContractsByCustomer(item.id)
        );

        if (contractStatus && Array.isArray(contractStatus)) {
          const totalContracts = contractStatus.length;
          const signedContracts = contractStatus.filter(
            (c) => c.status === 'signed'
          ).length;

          // Define o valor de isContractSigned como string simples
          if (totalContracts > 0) {
            if (signedContracts === totalContracts) {
              item['isContractSigned'] = 'SIM';
            } else if (signedContracts > 0) {
              // Para estado parcial, usamos um prefixo para identificar no SASS
              item[
                'isContractSigned'
              ] = `PARCIAL (${signedContracts}/${totalContracts})`;
            } else {
              item['isContractSigned'] = 'NÃO';
            }
          } else {
            item['isContractSigned'] = 'NÃO';
          }

          // Atualiza a flag de contrato gerado
          item['isContractGenerated'] = totalContracts > 0 ? 'SIM' : 'NÃO';
        } else {
          item['isContractSigned'] = 'NÃO';
          item['isContractGenerated'] = 'NÃO';
        }
      } catch (error) {
        console.error(
          `Erro ao verificar contratos do cliente ${item.id}:`,
          error
        );
        item['isContractSigned'] = 'ERRO'; // Estado de erro
        item['isContractGenerated'] = 'ERRO';
      }

      return item;
    });

    // Quando todas as promessas forem resolvidas, atualiza a tabela
    Promise.all(promises).then((processedData) => {
      this.dataSource.data = processedData;
      this.cdr.detectChanges();
    });

    return initialData;
  }

  public ngAfterViewInit(): void {
    this.loadList();
  }

  public _openEdit($event: any) {
    this.router.navigate(['customers/edit', $event.id], {
      state: { editParams: this.editParams },
    });
  }

  public _openNew($event: any) {
    this.router.navigate(['customers/new']);
  }

  public _openShow($event: any) {
    this.router.navigate(['customers/show', $event.id]);
  }

  public _select($event: any) {
    console.log($event);
  }

  public generateLicense($event: any) {
    if ($event.tenantId) {
      this.toastService.showWarn(
        MessagesEnum.CUSTOMER_LICENSE_ALREADY_GENERATED
      );
      return;
    }

    if (!$event.signedAt) {
      this.toastService.showWarn(
        MessagesEnum.CUSTOMER_WITHOUT_SIGNED_DOCUMENT_TO_GENERATE_LICENSE
      );
      return;
    }

    this.tenantService.createByCustomer($event?.id).subscribe((res: any) => {
      this.toastService.showSuccess(
        // `Licença gerada com sucesso!\n usuário: ${res['email']}\n senha: ${res['password']}`
        MessagesEnum.LICENSE_GENERATED_SUCESS.replace(
          ':user',
          res['email']
        ).replace(':password', res['password'])
      );
      this.loadList();
    });
  }

  public openContractsModal(element: any) {
    this.dialog.open(ContractsListDialogComponent, {
      width: '80%',
      data: {
        type: 'customer',
        entityInfo: {
          id: element.id,
          name: element.name || element.companyName,
          document: element.document,
          email: element.email,
          phone: element.phone || element.cellPhone,
        },
      },
    });
  }

  public OpenModalDocumentPhoto(element: any) {
    let documentPhoto;
    if (element.documentPhoto && typeof element.documentPhoto === 'string') {
      if (element.documentPhoto.startsWith('http')) {
        documentPhoto = element.documentPhoto;
      } else {
        documentPhoto = S3_URL_BASE + element.documentPhoto;
      }
    }
    this.dialog
      .open(DeviceTypeSelectDialogComponent, {
        width: '600px',
        data: { image: documentPhoto },
      })
      .afterClosed()
      .subscribe(async (data: any) => {
        if (data.image) {
          const imagem = new File(
            [await GlobalUtils.blobImageFromBase64String(data.image)],
            data.image?.toLowerCase().indexOf('png') != -1
              ? `filename.png`
              : `filename.jpeg`
          );
          this.service.updateDocumentPhoto(element.id, imagem).subscribe({
            next: (res: any) => {
              this.toastService.showSuccess('Imagem atualizada com Sucesso');
              this.loadList();
            },
            error: (er: any) => {
              this.toastService.showError(er);
            },
          });
        }
      });
  }

  public lock(customer: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: {
        title: `Bloquear cliente: ${GlobalUtils.getCustomerDisplayName(
          customer
        )}`,
        content: `Ao bloquear o cliente, ele não poderá acessar o sistema até ser desbloqueado. Deseja continuar?`,
        noText: 'CANCELAR',
        yesText: 'BLOQUEAR',
      },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          customer._lockLoading = true;
          this.service
            .changeLockStatus(customer.id!, true)
            .pipe(take(1))
            .subscribe({
              next: (res: any) => {
                this.toastService.showSuccess(
                  MessagesEnum.CUSTOMER_LOCK_SUCCESS
                );
                customer.isLock = true;
                customer.lockedAt = res.lockedAt;
                customer.unlockedAt = null;
                customer._lockLoading = false;
                this.cdr.detectChanges();
                this.loadList();
              },
              error: (e) => {
                customer._lockLoading = false;
                this.cdr.detectChanges();
                console.log(e);
              },
            });
        }
      },
    });
  }

  public unlock(customer: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: {
        title: `Desbloquear cliente: ${GlobalUtils.getCustomerDisplayName(
          customer
        )}`,
        content: `Ao desbloquear o cliente, ele poderá voltar a acessar o sistema normalmente. Deseja continuar?`,
        noText: 'CANCELAR',
        yesText: 'DESBLOQUEAR',
      },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          customer._lockLoading = true;
          this.service
            .changeLockStatus(customer.id!, false)
            .pipe(take(1))
            .subscribe({
              next: (res: any) => {
                this.toastService.showSuccess(
                  MessagesEnum.CUSTOMER_UNLOCK_SUCCESS
                );
                customer.isLock = false;
                customer.unlockedAt = res.unlockedAt;
                customer.lockedAt = null;
                customer._lockLoading = false;
                this.cdr.detectChanges();
                this.loadList();
              },
              error: (e) => {
                customer._lockLoading = false;
                this.cdr.detectChanges();
                console.log(e);
              },
            });
        }
      },
    });
  }

  public active(customer: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: {
        title: `Ativar cliente: ${GlobalUtils.getCustomerDisplayName(
          customer
        )}`,
      },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeActiveStatus(customer.id!, true)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(
                  MessagesEnum.CUSTOMER_ACTIVE_SUCCESS
                );
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public inactive(customer: any) {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: {
        title: `Desativar cliente: ${GlobalUtils.getCustomerDisplayName(
          customer
        )}`,
      },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.service
            .changeActiveStatus(customer.id!, false)
            .pipe(take(1))
            .subscribe({
              next: () => {
                this.toastService.showSuccess(
                  MessagesEnum.CUSTOMER_INACTIVE_SUCCESS
                );
                this.loadList();
              },
              error: (e) => console.log(e),
            });
        }
      },
    });
  }

  public key(customer: any) {
    if (!customer.tenantId) {
      this.toastService.showWarn(
        MessagesEnum.CUSTOMER_WITHOUT_LICENSE_TO_RESET_PASSWORD
      );
      return;
    }

    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: {
        title: MessagesEnum.RESET_PASSWORD_CUSTOMER_TITLE.replace(
          ':name',
          GlobalUtils.getCustomerDisplayName(customer)
        ),
      },
      component: DialogConfirmComponent,
      callBack: (data: any) => {
        if (data?.result) {
          this.tenantService
            .resetPasswordByCustomer(customer.id)
            .pipe(take(1))
            .subscribe({
              next: (response: any) => {
                const email = response?.email || customer.email || '';
                const password = response?.password || '';
                this.toastService.showSuccess(
                  `Anote as novas credenciais do cliente:<br><br>` +
                  `<b>Usuário:</b> ${email}<br>` +
                  `<b>Senha:</b> ${password}`,
                  {
                    ...this.toastService.modalProps,
                    title: 'Senha redefinida com sucesso',
                    enableHtml: true,
                    confirmButtonText: 'OK',
                    iconColor: '#4CAF50',
                  }
                );
                this.loadList();
              },
              error: (e) => {
                const raw = (e as any)?.error?.message || (e as any)?.message;
                const message = Array.isArray(raw) ? raw.join('; ') : (raw || 'Não foi possível redefinir a senha do cliente.');
                this.toastService.showError(message);
              },
            });
        }
      },
    });
  }

  public generateParcel() {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      component: CustomerParcelCESComponent,
      data: {
        type: TypeCesEnum.NEW,
        customerId: this.selection?.source._selected[0].id,
      },
      dialogConfig: {
        minWidth: '800px',
        maxWidth: '1800px',
        minHeight: '420px',
        maxHeight: '900px',
        width: 'auto',
        height: 'auto',
      },
      callBack: (data: any) => {
        if (data && data.billItems && data.billItems.length > 0) {
          this.generateParcelExecute(data, {
            updateReason: data.observation || '',
          });
        }
      },
    });
  }

  public generateContract() {
    const selectedCustomer = this.getSelectedCustomer();
    if (!selectedCustomer) {
      this.toastService.showWarn('Selecione um cliente para gerar o contrato');
      return;
    }

    const dialogRef = this.dialog.open(ContractsDialogComponent, {
      width: '80%',
      maxWidth: '95vw',
      maxHeight: '95vh',
      data: {
        entity: {
          id: selectedCustomer.id,
          name: GlobalUtils.getCustomerDisplayName(selectedCustomer),
          document: selectedCustomer.document,
          cpf: selectedCustomer.cpf,
          cnpj: selectedCustomer.cnpj,
          email: selectedCustomer.email,
          phone: selectedCustomer.phone,
          cellPhone: selectedCustomer.cellPhone,
          partnerId: selectedCustomer.partnerId,
          consultantId: selectedCustomer.consultantId,
          representativeId: selectedCustomer.representativeId,
          counterId: selectedCustomer.counterId,
        },
        entityType: 'customer',
        entityService: this.customerContractGenerationService,
        generationService: this.customerContractGenerationService,
      },
      disableClose: false,
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        console.log(
          'Modal de contrato fechado com sucesso. Recarregando lista.'
        );
        this.loadList();
      } else {
        console.log('Modal de contrato fechado sem concluir a geração.');
      }
    });
  }

  private getSelectedCustomer(): ICustomer | null {
    if (
      !this.selection?.source?._selected ||
      this.selection.source._selected.length !== 1
    ) {
      return null;
    }
    return this.selection.source._selected[0] as ICustomer;
  }

  public generateParcelExecute(data: any, dataReason: any) {
    this.toastService.showSuccess(MessagesEnum.SUCCESS_CREATED);
  }

  public updatePlans() {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      component: CustomerToUpdatePlansComponent,
      data: {},
      dialogConfig: {
        minWidth: '800px',
        maxWidth: '1800px',
        minHeight: '420px',
        maxHeight: '900px',
        width: 'auto',
        height: 'auto',
      },
      callBack: (data: any) => { },
    });
  }

  public openSettings(element: any): void {
    GlobalUtils.openDialog({
      dialog: this.dialog,
      dialogConfig: {
        width: 'auto',
        minWidth: '400px',
        height: 'auto',
        minHeight: '400px',
        enterAnimationDuration: '0ms',
        exitAnimationDuration: '0ms',
        disableClose: false,
      },
      component: ComponentOptionsComponent,
      data: { type: 'customer', select: true, customer: element },
      callBack: (data: any) => { },
    });
  }

  /**
   * Método para resetar todos os filtros
   */
  public resetFilter(): void {
    // Limpar valores do formulário
    this.filterFormValues = null;
    localStorage.removeItem('customerFilterFormValues');

    // Resetar estado de filtros
    this.isAdvancedFilterActive = false;
    this.lastAppliedFilter = null;
    this.params = { limit: 25, page: 0, filter: '' };

    // Limpar dados persistidos
    localStorage.removeItem('customerParams');
    localStorage.removeItem('customerIsAdvancedFilterActive');
    localStorage.removeItem('customerLastAppliedFilter');

    // Resetar também no serviço estático
    CustomerService.isAdvancedFilterActive = false;
    CustomerService.lastAppliedFilter = null;
    CustomerService.params = { limit: 25, page: 0, filter: '' };

    // Criar um novo FormGroup para resetar os valores de todos os campos, incluindo ATIVO
    if (this.filterFields && this.filterFields.length > 0) {
      const resetFields = this.filterFields.map((field) => {
        // Criamos uma cópia do campo para preservar outras propriedades
        const fieldCopy = { ...field };

        // Para cada tipo de campo, definimos o valor padrão adequado
        if (field.type === TypeComponentFieldEnum.SLIDE_TOGGLE) {
          // Usamos o valor padrão definido para o campo (ou false se não existir)
          // Para isActive, queremos manter true por padrão
          if (field.name === 'isActive') {
            fieldCopy.value = true; // isActive deve ser true por padrão
          } else {
            fieldCopy.value = false; // outros toggles são false por padrão
          }
        }
        // Para campos selects, especialmente os SELECT_CRUD
        else if (
          field.type === TypeComponentFieldEnum.SELECT ||
          field.type === TypeComponentFieldEnum.SELECT_CRUD
        ) {
          fieldCopy.value = '';
        }
        // Para todos os outros campos, definimos como string vazia
        else {
          fieldCopy.value = '';
        }

        return fieldCopy;
      });

      // Atualizar os campos do filtro com os valores limpos
      this.filterFields = resetFields;
    }

    // Recarregar lista com filtros limpos
    this.loadList(true);

    // Forçar o Angular a detectar as mudanças
    this.cdr.detectChanges();
  }

  public async generateAcess() {
    const selectedCustomer = this.getSelectedCustomer();
    if (!selectedCustomer) {
      this.toastService.showWarn('Selecione um cliente');
      return;
    }

    const { CustomerAcessCESComponent } = await import(
      '../../customer-acess/crud-ces/crud-ces.component'
    );

    GlobalUtils.openDialog({
      dialog: this.dialog,
      data: {
        customerId: selectedCustomer.id,
        title: `Gerar Acesso - ${GlobalUtils.getCustomerDisplayName(
          selectedCustomer
        )}`,
        type: TypeCesEnum.NEW,
        element: {
          customers: [selectedCustomer.id],
          type: 1,
        },
      },
      component: CustomerAcessCESComponent,
      dialogConfig: {
        minWidth: '1200px',
        maxWidth: '2000px',
        width: 'auto',
      },
      callBack: (data: any) => {
        if (data?.result) {
          this.loadList();
        }
      },
    });
  }

  public async customerAcess() {
    const { CustomerAcessIndexComponent } = await import(
      '../../customer-acess/crud-index/crud-index.component'
    );

    GlobalUtils.openDialog({
      dialog: this.dialog,
      component: CustomerAcessIndexComponent,
      data: { Element },
      dialogConfig: {
        minWidth: '1200px',
        maxWidth: '2000px',
        width: 'auto',
      },
      callBack: (data: any) => {
        if (data?.result) {
          this.loadList();
        }
      },
    });
  }

  public moneyAction(element: any) {
    this.router.navigate(['accounts-receivable/show', element.id]);
  }

  public viewClientPlans(element: any) {
    // Método a ser implementado pelas classes que estendem esta classe
    console.log('Método viewClientPlans não implementado na classe base');
  }


  public generateReports() {
    const items = this.selection?.source._selected;
    if (items.length <= 0) {
      this.toastService.showInfo("Selecione pelo menos uma parcela");
      return;
    }

    const columns = [
      { header: 'Código', key: 'code', type: 'text' },
      { header: 'Nome', key: 'name', type: 'text' },
      { header: 'Cidade', key: 'city', type: 'text' },
      { header: 'Solução', key: 'system', type: 'text' },
      { header: 'Parceiros', key: 'partners', type: 'text' },
      { header: 'Valor da Mensalidade', key: 'value', type: 'currency', align: 'center', nowrap: true },
      { header: 'Cadastro', key: 'createdAt', type: 'text', minWidth: '115px' },
    ];

    const rows = items.flatMap((i: any) => {
      const plans = Array.isArray(i.customerPlans) ? i.customerPlans : [];

      if (plans.length === 0) {
        return [{
          code: i.code ?? '',
          name: i.name ?? i.companyName ?? i.tradingName ?? '',
          city: i.city ?? '',
          system: '-',
          partners: this.getPartners(i),
          value: 0,
          createdAt: i.createdAt ?? '',
        }];
      }

      return plans.map((p: any) => ({
        code: i.code ?? '',
        name: i.name ?? i.companyName ?? i.tradingName ?? '',
        city: i.city ?? '',
        partners: this.getPartners(i),
        system: p?.system?.name ?? '',
        value: p?.monthlyValue ?? 0,
        createdAt: i.createdAt ?? '',
      }));
    });
    const loadingRef = this.toastService.showInfo('Gerando relatório PDF...');
    const payload = {
      title: 'Relatório de Clientes',
      columns,
      rows,
      showTotal: true,
      totalLabel: 'TOTAL',
      totalField: 'value',
    };

    this.accountDashboardService
      .generateReportPdf(payload)
      .pipe(finalize(() => loadingRef))
      .subscribe({
        next: (pdfBlob: Blob) => {

          const fileName = `relatorio-clientes-${new Date().toISOString().slice(0, 10)}.pdf`;

          this.dialog.open(ReportPreviewComponent, {
            data: {
              blob: pdfBlob,
              fileName: fileName
            },
            width: '80vw',
            height: '90vh'
          });

          this.toastService.showSuccess("PRÉ-VISUALIZAÇÃO GERADA");
        },
        error: (err) => {
          console.error('Erro ao gerar relatório PDF:', err);
          this.toastService.showError("Erro ao gerar PDF", err);
        }
      });
  }

  private getPartners(customer: any): string {
    const relations = [
      { label: 'Partner', obj: customer.partner },
      { label: 'Consultant', obj: customer.consultant },
      { label: 'Representative', obj: customer.representative },
      { label: 'Counter', obj: customer.counter },
      { label: 'Unit', obj: customer.unit }
    ];

    return relations
      .filter(item => item.obj && item.obj.name)
      .map(item => item.obj?.name || item.obj?.tradingName || item.obj?.companyName)
      .join(', ');
  }
}
